import { useParams, useNavigate } from 'react-router-dom'
import { SECTIONS } from '../../data/sections'
import { DESC } from '../../data/descriptors'
import type { SectionId, Category, Tool } from '../../types/tools'
import { useAutoSave } from '../../hooks/useAutoSave'
import { useToolData } from '../../hooks/useToolData'
import NavBack from '../base/NavBack'
import SectionBlock from '../base/SectionBlock'
import ToolHeader from '../base/ToolHeader'
import ToolDescriptor from '../base/ToolDescriptor'
import InspirationSection from './InspirationSection'
import TeamViewer from './TeamViewer'
import ToolRenderer from '../tools/ToolRenderer'

export default function ToolPage() {
  const { sectionKey, categoryKey, toolKey } = useParams<{
    sectionKey: string
    categoryKey: string
    toolKey: string
  }>()
  const navigate = useNavigate()

  const section = SECTIONS[sectionKey as SectionId]
  const category: Category | undefined = section
    ? (section.cats as unknown as Record<string, Category>)[categoryKey!]
    : undefined
  const tool: Tool | undefined = category
    ? (category.tools as Record<string, Tool>)[toolKey!]
    : undefined
  const toolName = tool?.l ?? toolKey ?? 'Unknown Tool'

  const { data, clearAll } = useToolData(toolKey ?? '')
  const { save } = useAutoSave(toolKey ?? '')

  if (!section || !category || !tool) {
    return (
      <div className="tool-page" data-testid="tool-not-found">
        <p>Tool not found.</p>
        <NavBack label="Back" onClick={() => navigate('/')} />
      </div>
    )
  }

  const desc = DESC[toolKey!]

  const handleSave = () => {
    save(data)
  }

  return (
    <div className="tool-page" data-testid="tool-page">
      <NavBack label={category.label} onClick={() => navigate(`/category/${sectionKey}/${categoryKey}`)} />

      {/* Section 1: Inspiration */}
      <SectionBlock number={1} title="Inspiration" variant="inspiration">
        <InspirationSection toolKey={toolKey!} categoryKey={categoryKey!} />
      </SectionBlock>

      {/* Section 2: Application */}
      <SectionBlock number={2} title="Application">
        <ToolHeader
          categoryLabel={category.label}
          toolName={toolName}
          onClear={clearAll}
          onSave={handleSave}
          onMarkComplete={() => {/* TODO: mark complete */}}
        />

        {desc && (
          <ToolDescriptor why={desc.why} what={desc.what} how={desc.how} when={desc.when} />
        )}

        <div data-testid="tool-renderer-area">
          <ToolRenderer toolId={toolKey!} />
        </div>
      </SectionBlock>

      {/* Section 3: Your Team */}
      <SectionBlock number={3} title="Your Team" variant="team">
        <TeamViewer toolId={toolKey!} />
      </SectionBlock>
    </div>
  )
}
